import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { auth } from "../config/firebase";

// Custom handler for Firebase Auth email links: verify email, reset password,
// and undo an email change.
//
// Firebase will not let this project point its own emails here — the action URL
// is locked to the Firebase default (see backend/authpages/PLAN.md). So the
// backend generates the action code itself and mails a link to this page. The
// code is an ordinary Firebase oobCode either way, so links from Firebase's own
// emails work here too.
//
// The design deliberately mirrors the Solutions customer emails and the
// server-rendered pages at auth.muslimplus.org, so arriving from an email does
// not feel like landing somewhere unrelated.

const T = {
  page: "#eeeae1",
  ink: "#1a1713",
  body: "#3a332a",
  muted: "#6b6258",
  faint: "#8a8178",
  onDarkSub: "#c8bfa8",
  eyebrowOnDark: "#9a9288",
  cream: "#faf7f1",
  gold: "#bf9a2f",
  goldBright: "#d4ad3f",
  goldLink: "#8a6f1f",
  hairline: "#e8dfce",
  ok: "#2e9e5b",
  bad: "#c0392b",
  sans: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  serif: "Georgia, 'Times New Roman', Times, serif",
};

const MIN_PASSWORD_LENGTH = 8;
const APP_SCHEME = "muslim";

const S = {
  wrap: { background: T.page, minHeight: "100vh", fontFamily: T.sans, color: T.body },
  inner: { maxWidth: 600, margin: "0 auto", padding: "30px 12px 48px" },
  card: { background: "#fff", boxShadow: "0 1px 2px rgba(26,23,19,.06), 0 8px 28px rgba(26,23,19,.07)" },
  header: { background: T.ink, padding: "30px 26px" },
  brandRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16 },
  wordmark: { fontFamily: T.serif, fontSize: 18, color: T.cream, textDecoration: "none" },
  eyebrowDark: {
    fontSize: 10.5, fontWeight: 700, letterSpacing: ".22em",
    textTransform: "uppercase", color: T.eyebrowOnDark, textAlign: "right",
  },
  h1: { fontFamily: T.serif, fontSize: 30, lineHeight: "37px", fontWeight: 400, color: T.cream, margin: "24px 0 0" },
  sub: { fontSize: 14, color: T.onDarkSub, marginTop: 6, wordBreak: "break-all" },
  content: { padding: "30px 26px 32px" },
  lead: { fontSize: 16, lineHeight: "26px", color: T.body, margin: "0 0 16px" },
  note: { fontSize: 12.5, lineHeight: "21px", color: T.faint, margin: "0 0 16px" },
  panel: { background: T.cream, borderLeft: `3px solid ${T.gold}`, padding: "18px 20px", margin: "0 0 24px" },
  panelLabel: { fontSize: 11, fontWeight: 700, letterSpacing: ".13em", textTransform: "uppercase", color: T.faint },
  panelValue: { fontSize: 17, fontWeight: 700, color: T.ink, paddingTop: 6, wordBreak: "break-all" },
  label: {
    display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".13em",
    textTransform: "uppercase", color: T.faint, marginBottom: 7,
  },
  input: {
    width: "100%", padding: "13px 14px", fontFamily: T.sans, fontSize: 16,
    color: T.ink, background: "#fff", border: `1px solid ${T.hairline}`,
    borderRadius: 4, marginBottom: 18, boxSizing: "border-box",
  },
  fieldNote: { fontSize: 12.5, color: T.faint, margin: "-12px 0 18px" },
  btn: {
    display: "block", width: "100%", border: 0, borderRadius: 4, cursor: "pointer",
    background: T.gold, color: "#fff", textAlign: "center", textDecoration: "none",
    fontFamily: T.sans, fontSize: 15.5, fontWeight: 700, padding: "16px 24px", marginTop: 4,
  },
  btnSecondary: {
    display: "block", width: "100%", borderRadius: 4, cursor: "pointer",
    background: "transparent", color: T.goldLink, border: `1px solid ${T.hairline}`,
    textAlign: "center", textDecoration: "none", fontFamily: T.sans,
    fontSize: 15.5, fontWeight: 700, padding: "16px 24px", marginTop: 10, boxSizing: "border-box",
  },
  alert: {
    background: "#fdf0ee", borderLeft: `3px solid ${T.bad}`, padding: "13px 16px",
    marginBottom: 22, fontSize: 13.5, lineHeight: "21px", color: "#8c2f22",
  },
  mark: {
    width: 46, height: 46, borderRadius: "50%", display: "flex",
    alignItems: "center", justifyContent: "center", fontSize: 24,
    color: "#fff", marginBottom: 20,
  },
  divider: { border: 0, borderTop: `1px solid ${T.hairline}`, margin: "26px 0 22px" },
  footer: { padding: "18px 26px", fontSize: 12, lineHeight: "19px", color: T.faint },
  link: { color: T.goldLink },
};

function Shell({ eyebrow, heading, sub, children }) {
  return (
    <div style={S.wrap}>
      <div style={S.inner}>
        <div style={S.card}>
          <div style={S.header}>
            <div style={S.brandRow}>
              <a style={S.wordmark} href="/">
                Muslim<span style={{ color: T.goldBright }}>+</span>
              </a>
              <div style={S.eyebrowDark}>{eyebrow}</div>
            </div>
            <h1 style={S.h1}>{heading}</h1>
            {sub ? <div style={S.sub}>{sub}</div> : null}
          </div>
          <div style={S.content}>{children}</div>
        </div>
        <div style={S.footer}>
          Questions? Write to{" "}
          <a style={S.link} href="mailto:support@muslimplus.org">
            support@muslimplus.org
          </a>
          .<br />
          This page was opened from a link we emailed you. It works once, and only for a short while.
        </div>
      </div>
    </div>
  );
}

function Result({ eyebrow, heading, sub, kind, lead, note, actions }) {
  return (
    <Shell eyebrow={eyebrow} heading={heading} sub={sub}>
      <div style={{ ...S.mark, background: kind === "ok" ? T.ok : T.bad }}>
        {kind === "ok" ? "✓" : "!"}
      </div>
      <p style={S.lead}>{lead}</p>
      {note ? <p style={S.note}>{note}</p> : null}
      {actions}
    </Shell>
  );
}

function AppButtons() {
  return (
    <>
      <a style={S.btn} href={`${APP_SCHEME}://`}>
        Open Muslim+
      </a>
      <a style={S.btnSecondary} href="/">
        Go to muslimplus.org
      </a>
    </>
  );
}

/**
 * Expired, already used, and malformed codes are indistinguishable from the
 * outside — Firebase burns the code on use — so one message covers all three
 * rather than guessing which happened.
 */
function expiredCopy(what) {
  return {
    heading: "This link no longer works",
    sub: "It has expired, or it has already been used",
    lead: `We could not use this link to ${what}. Links like this one expire after a while, and each one only works a single time.`,
    note: "Ask for a new link from the Muslim+ app, and open it from the newest email we sent you. Older emails in your inbox will not work.",
  };
}

export default function AuthAction() {
  const [params] = useSearchParams();
  const mode = params.get("mode") || "";
  const oobCode = params.get("oobCode") || "";

  const [state, setState] = useState({ status: "working" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!mode || !oobCode) {
        if (!cancelled) setState({ status: "badRequest" });
        return;
      }
      try {
        if (mode === "resetPassword") {
          // A lookup, not a redemption: the code survives if the person opens
          // the link and comes back later.
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (!cancelled) setState({ status: "resetForm", email });
          return;
        }
        if (mode === "verifyEmail" || mode === "verifyAndChangeEmail") {
          await applyActionCode(auth, oobCode);
          if (!cancelled) {
            setState({ status: "verified", isChange: mode === "verifyAndChangeEmail" });
          }
          return;
        }
        if (mode === "recoverEmail") {
          await applyActionCode(auth, oobCode);
          if (!cancelled) setState({ status: "recovered" });
          return;
        }
        if (!cancelled) setState({ status: "unsupported" });
      } catch (err) {
        if (!cancelled) {
          setState({ status: err?.code === "auth/user-disabled" ? "disabled" : "expired" });
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  async function onSubmit(event) {
    event.preventDefault();
    setFormError("");

    if (!password || password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Your new password needs to be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setFormError("The two passwords do not match. Type them again.");
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setState({ status: "resetDone", email: state.email });
    } catch (err) {
      if (err?.code === "auth/weak-password") {
        setFormError("That password is too weak. Try a longer one.");
      } else if (err?.code === "auth/expired-action-code" || err?.code === "auth/invalid-action-code") {
        setState({ status: "expired" });
      } else {
        setFormError("We could not update your password just now. Try the link again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const whatFor = mode === "resetPassword" ? "reset your password" : "confirm your email address";
  const eyebrowFor =
    mode === "resetPassword" ? "Password reset" :
      mode === "recoverEmail" ? "Email change" : "Verify email";

  switch (state.status) {
    case "working":
      return (
        <Shell eyebrow="Account" heading="One moment">
          <p style={S.lead}>Checking your link…</p>
        </Shell>
      );

    case "badRequest":
      return (
        <Result
          eyebrow="Account"
          heading="We cannot open this page"
          kind="bad"
          lead="This address is missing the part that tells us which account it belongs to, so there is nothing for us to do here."
          note="If you got here from an email, try tapping the link again rather than copying it, so the whole address comes through."
          actions={<a style={S.btn} href="/">Go to muslimplus.org</a>}
        />
      );

    case "unsupported":
      return (
        <Result
          eyebrow="Account"
          heading="We cannot open this page"
          kind="bad"
          lead="This link is for something Muslim+ does not handle on the web."
          actions={<a style={S.btn} href="/">Go to muslimplus.org</a>}
        />
      );

    case "disabled":
      return (
        <Result
          eyebrow={eyebrowFor}
          heading="We could not finish"
          kind="bad"
          lead="This account has been disabled, so we could not finish. Write to support@muslimplus.org and we will look into it."
          actions={<a style={S.btn} href="mailto:support@muslimplus.org">Email support</a>}
        />
      );

    case "expired": {
      const copy = expiredCopy(whatFor);
      return (
        <Result
          eyebrow={eyebrowFor}
          heading={copy.heading}
          sub={copy.sub}
          kind="bad"
          lead={copy.lead}
          note={copy.note}
          actions={<a style={S.btn} href={`${APP_SCHEME}://`}>Open Muslim+ and try again</a>}
        />
      );
    }

    case "verified":
      return (
        <Result
          eyebrow="Verify email"
          heading={state.isChange ? "Your new email is confirmed" : "Your email is verified"}
          kind="ok"
          lead={
            state.isChange ?
              "That address is now the email on your Muslim+ account. Sign in with it from now on." :
              "Thank you. Your email address is confirmed, and your Muslim+ account is ready to use."
          }
          note="You can close this page and go back to the app."
          actions={<AppButtons />}
        />
      );

    case "recovered":
      return (
        <Result
          eyebrow="Email change"
          heading="Your email has been restored"
          kind="ok"
          lead="Your Muslim+ account is back on your original email address. Sign in with that address."
          note="If you did not ask for the change in the first place, reset your password now so whoever made it cannot get back in."
          actions={<a style={S.btn} href={`${APP_SCHEME}://`}>Open Muslim+</a>}
        />
      );

    case "resetDone":
      return (
        <Result
          eyebrow="Password reset"
          heading="Your password is updated"
          sub={state.email}
          kind="ok"
          lead="You can now sign in to Muslim+ with your new password."
          note="For your security you have been signed out everywhere else. Sign in again on your other devices."
          actions={
            <>
              <a style={S.btn} href="/login">Sign in</a>
              <a style={S.btnSecondary} href={`${APP_SCHEME}://`}>Open the Muslim+ app</a>
            </>
          }
        />
      );

    case "resetForm":
      return (
        <Shell
          eyebrow="Password reset"
          heading="Choose a new password"
          sub={state.email ? `For ${state.email}` : undefined}
        >
          {formError ? <div style={S.alert}>{formError}</div> : null}
          {state.email ? (
            <div style={S.panel}>
              <div style={S.panelLabel}>Account</div>
              <div style={S.panelValue}>{state.email}</div>
            </div>
          ) : null}
          <form onSubmit={onSubmit}>
            {/* Carries the account to the browser's password manager, which
                otherwise has nothing to attach the saved credential to. */}
            <input
              type="email"
              value={state.email || ""}
              autoComplete="username"
              readOnly
              hidden
            />
            <label style={S.label} htmlFor="password">New password</label>
            <input
              style={S.input}
              type="password"
              id="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p style={S.fieldNote}>
              At least {MIN_PASSWORD_LENGTH} characters. A few unrelated words make a
              stronger password than one clever word.
            </p>
            <label style={S.label} htmlFor="confirm">Confirm new password</label>
            <input
              style={S.input}
              type="password"
              id="confirm"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <button style={{ ...S.btn, opacity: submitting ? 0.7 : 1 }} type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save new password"}
            </button>
          </form>
          <hr style={S.divider} />
          <p style={S.note}>
            Signing in on other devices will not be affected until you next sign in there.
            If you did not ask to reset your password, you can close this page and nothing
            will change.
          </p>
        </Shell>
      );

    default:
      return null;
  }
}
